import { useCallback, useContext, useEffect, useState } from "react";
import { useCompanyLicensesQuery } from "../../graphql/Queries/Licenses/GetCompanyLicenses";
import { getPrintersRegistry } from "../../zeus-client/gatewaysApis";
import { DATASOURCES, DetailedLicenseData } from "../../graphql";
import { Notifier } from "../../view/Notifier";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { ConfigureAnalyzeSassPrintersList } from "./AnalyzeInterface";

interface AllSaaSPrinters {
    loadingData: boolean;
    listOfPrinters: ConfigureAnalyzeSassPrintersList[];
    printerCount: number;
    gatewayPrinterCount: number;
    printServerPrinterCount: number;
    streamlineProLicenceCount: number;
}

interface UseGetAllSaaSPrintersOptions {
    includePrinterCount?: boolean;
    includeGatewayCount?: boolean;
    includePrintServerCount?: boolean;
    includeStreamlineCount?: boolean;
}

export const useGetAllSaaSPrinters = (options?: UseGetAllSaaSPrintersOptions) => {
    const {
        includePrinterCount = false,
        includeGatewayCount = false,
        includePrintServerCount = false,
        includeStreamlineCount = false,
    } = options || {};

    const { companyLicenses } = useCompanyLicensesQuery();
    const { t } = useContext(ApplicationContext);

    const [state, setState] = useState<AllSaaSPrinters>({
        loadingData: false,
        listOfPrinters: [],
        printerCount: 0,
        gatewayPrinterCount: 0,
        printServerPrinterCount: 0,
        streamlineProLicenceCount: 0,
    });

    const findStreamlineProLicenseCount = useCallback(() => {
        if (!companyLicenses?.licenses) {
            return;
        }
        const streamlineProLicenses = companyLicenses.licenses.filter(
            (license) =>
                license.package.product?.features?.some((feature) =>
                    feature.key.startsWith("streamline_pro")
                ) &&
                license.state !== "expired" &&
                license.state !== "not_started"
        );
        setState((prevState) => ({
            ...prevState,
            streamlineProLicenceCount: streamlineProLicenses.length,
        }));
    }, [companyLicenses]);

    const getChannel = useCallback((dataSources: string[]) => {
        const lowerCaseMap = dataSources.map((source) => source.toLowerCase());
        if (lowerCaseMap.includes("gateway") && lowerCaseMap.includes("print server")) {
            return "Print Server and Gateway";
        } else if (lowerCaseMap.includes("iot")) {
            return DATASOURCES.IOT;
        } else if (lowerCaseMap.includes("print server")) {
            return DATASOURCES.Print_Server;
        } else {
            return DATASOURCES.Unknown;
        }
    }, []);

    const findIsMachineEntitled = useCallback(
        (printerSerial: string, detailedLicense: DetailedLicenseData[], isAny: boolean = false) => {
            const isAnyPrinterAllowed = detailedLicense.find(
                (license) =>
                    license.machineEntitlements?.some((machine) => machine.serial === null) &&
                    license.state === "active"
            );
            const licensePerPrinter = detailedLicense.find(
                (license) =>
                    (!isAny &&
                        license.machineEntitlements?.some(
                            (machine) => machine.serial === printerSerial
                        ) &&
                        license.state === "active") ||
                    (isAny &&
                        license.state === "active" &&
                        license.package.product?.features?.some(
                            (feature) =>
                                feature.key === "analyze_user" ||
                                feature.key.startsWith("streamline_pro")
                        ) &&
                        license.machineEntitlements?.some(
                            (machine) => machine.serial === printerSerial
                        ))
            );

            if (isAnyPrinterAllowed) {
                return {
                    licenseName: isAnyPrinterAllowed.package.name,
                    startDate: isAnyPrinterAllowed.startDate.toString() ?? "",
                    endDate: isAnyPrinterAllowed.endDate?.toString() ?? "",
                };
            }

            if (licensePerPrinter) {
                return {
                    licenseName: licensePerPrinter.package.name,
                    startDate: licensePerPrinter.startDate.toString() ?? "",
                    endDate: licensePerPrinter.endDate?.toString() ?? "",
                };
            }
        },
        []
    );

    const getLicenseDetailsPerPrinter = useCallback(
        async (printerSerial: string) => {
            const analyzeLicenses = companyLicenses?.licenses.filter(
                (license) => license.package.product?.applicationType?.id === "analyze_user"
            );
            const anyAppTypes = companyLicenses?.licenses.filter(
                (license) => license.package.product?.applicationType?.id === "any"
            );
            if (analyzeLicenses && analyzeLicenses.length > 0) {
                return await findIsMachineEntitled(printerSerial, analyzeLicenses);
            }

            if (anyAppTypes && anyAppTypes.length > 0) {
                return await findIsMachineEntitled(printerSerial, anyAppTypes, true);
            }
        },
        [companyLicenses, findIsMachineEntitled]
    );

    const listGatewayClients = useCallback(async (): Promise<
        ConfigureAnalyzeSassPrintersList[]
    > => {
        let totalClients: ConfigureAnalyzeSassPrintersList[] = [];
        try {
            const response = await getPrintersRegistry();
            totalClients = await Promise.all(
                response.map(async (client) => {
                    const printerLicense = await getLicenseDetailsPerPrinter(client.serial);
                    return {
                        serialNumber: client.serial,
                        printerModel: client.printerType,
                        license: printerLicense?.licenseName ?? "",
                        licenseStart: printerLicense?.startDate ?? "",
                        licenseExpiry: printerLicense?.endDate ?? "",
                        channel: getChannel(client.dataSources),
                        location: "",
                        tag: printerLicense?.licenseName ? "SaaS" : "",
                    };
                })
            );
        } catch (error) {
            Notifier.fail(t("configure_analyze.configure_analyze_sass.api_error_title"));
        }
        return totalClients;
    }, [getLicenseDetailsPerPrinter, getChannel, t]);

    const initiateList = useCallback(async () => {
        setState((prev) => ({ ...prev, loadingData: true }));
        try {
            const results = await listGatewayClients();
            const gatewayClients = results.filter((rs) => rs.channel?.includes("Gateway"));
            const printServerClients = results.filter((rs) => rs.channel === "Print Server");

            setState((prevState) => ({
                ...prevState,
                listOfPrinters: results,
                gatewayPrinterCount: gatewayClients.length,
                printServerPrinterCount: printServerClients.length,
                printerCount: results.length,
                loadingData: false,
            }));
        } catch (error) {
            Notifier.fail(t("configure_analyze.configure_analyze_sass.api_error_title"));
            setState((prevState) => ({
                ...prevState,
                loadingData: false,
            }));
        }
    }, [listGatewayClients, t]);

    useEffect(() => {
        void initiateList();
        void findStreamlineProLicenseCount();
    }, [initiateList, findStreamlineProLicenseCount]);

    const {
        loadingData,
        listOfPrinters,
        printerCount,
        gatewayPrinterCount,
        printServerPrinterCount,
        streamlineProLicenceCount,
    } = state;

    const result: Record<string, unknown> = {
        loadingData,
        listOfPrinters,
    };

    if (includePrinterCount) {
        result.printerCount = printerCount;
    }

    if (includeGatewayCount) {
        result.gatewayPrinterCount = gatewayPrinterCount;
    }

    if (includePrintServerCount) {
        result.printServerPrinterCount = printServerPrinterCount;
    }

    if (includeStreamlineCount) {
        result.streamlineProLicenceCount = streamlineProLicenceCount;
    }

    return result;
};
